import { Center, Grid, Group, Loader, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconBellCheck } from '@tabler/icons-react';
import { useState } from 'react';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useClients } from 'src/clients/hooks/useClients';
import AdvisorScopeDropdown from 'src/notifications/components/AdvisorScopeDropdown';
import { useNotificationAdvisors } from 'src/notifications/hooks/useNotificationAdvisors';
import { useNotifications } from 'src/notifications/hooks/useNotifications';
import { ALL_GROUPS, filterNotifications } from 'src/notifications/notificationFilters';
import type { Notification } from 'src/notifications/types/Notification';
import { useNotificationDrawerStore } from 'src/notifications/useNotificationDrawerStore';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import DashboardTile from './DashboardTile';
import { outstandingByGroup, outstandingNotifications } from './dashboardRows';
import UpcomingMeetingsRail from './UpcomingMeetingsRail';

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function outstandingContent(
  isLoading: boolean,
  groups: [string, Notification[]][],
  clientNameOf: (clientId: string | null) => string | null,
  onViewAll: (group: string) => void,
) {
  if (isLoading) {
    return (
      <Center mih={160}>
        <Loader size="sm" />
      </Center>
    );
  }
  if (groups.length === 0) {
    return (
      <Center mih={160}>
        <Stack align="center" gap="xs">
          <ThemeIcon variant="light" color="gray" radius="xl" size={48}>
            <IconBellCheck size={24} />
          </ThemeIcon>
          <Text fw={600} c="gray.8">
            You are all caught up
          </Text>
        </Stack>
      </Center>
    );
  }
  return (
    <Grid gap="sm">
      {groups.map(([label, group]) => (
        <Grid.Col key={label} span={{ base: 12, sm: 6 }}>
          <DashboardTile
            label={label}
            notifications={group}
            clientNameOf={clientNameOf}
            onViewAll={() => onViewAll(label)}
          />
        </Grid.Col>
      ))}
    </Grid>
  );
}

export default function DashboardPage() {
  const { currentUser } = useCurrentUser();
  const { data, isLoading } = useNotifications();
  const { advisors, advisorOf } = useNotificationAdvisors(true);
  const { clients } = useClients({}, { enabled: true });
  const openDrawer = useNotificationDrawerStore(state => state.open);
  const [advisor, setAdvisor] = useState<string | null>(null);

  const clientName = new Map(
    clients.map(client => [
      client.id,
      formatName(client.firstName, client.lastName, NameFormat.NATURAL),
    ]),
  );
  const clientNameOf = (clientId: string | null): string | null =>
    clientId ? (clientName.get(clientId) ?? null) : null;

  const notifications = data ?? [];
  const outstanding = outstandingNotifications(
    filterNotifications(notifications, { status: 'all', group: ALL_GROUPS, advisor }, advisorOf),
  );
  const groups = outstandingByGroup(outstanding);
  const pivotItems = outstandingNotifications(
    filterNotifications(
      notifications,
      { status: 'all', group: ALL_GROUPS, advisor: null },
      advisorOf,
    ),
  );

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={2}>
            {greeting()}
            {currentUser?.firstName ? `, ${currentUser.firstName}` : ''}
          </Title>
          <Text size="sm" c="gray.6">
            {today}
          </Text>
        </Stack>
        <AdvisorScopeDropdown
          notifications={pivotItems}
          advisors={advisors}
          advisorOf={advisorOf}
          value={advisor}
          onChange={setAdvisor}
        />
      </Group>

      <Grid gap="lg">
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack gap="sm">
            <Title order={4}>Upcoming meetings</Title>
            <UpcomingMeetingsRail />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Stack gap="sm">
            <Title order={4}>Outstanding</Title>
            {outstandingContent(isLoading, groups, clientNameOf, group =>
              openDrawer({ status: 'all', group, advisor }),
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
